import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { getCategoryColor } from "@/lib/categoryColors";
import { getValueDisplay } from "@/components/shared/offerHelpers";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { BottomNav } from "@/components/shared/Navigation";
import { trackDealViewed } from "@/lib/firebase";
import TooltipOverlay from "@/components/shared/TooltipOverlay";
import { useAuthGate } from "@/hooks/useAuthGate";
import AuthGateSheet from "@/components/shared/AuthGateSheet";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { trackPageView, trackEvent, trackError } from "@/lib/track";
import { trackOfferViewed, trackOfferSaved, trackOfferUnsaved } from "@/lib/events";
import { useCopyBank } from "@/hooks/useCopyBank";
import { Loader2, ChevronRight } from "lucide-react";

import OfferDetailContext from "@/components/offer/OfferDetailContext";
import OfferHero from "@/components/offer/OfferHero";
import SubmitDealSheet from "@/components/offers/SubmitDealSheet";
import OfferSummaryCard from "@/components/offer/OfferSummaryCard";
import OfferDetailsCard from "@/components/offer/OfferDetailsCard";
import OfferConfidenceCard from "@/components/offer/OfferConfidenceCard";
import OfferVerificationSection from "@/components/offer/OfferVerificationSection";
import OfferActionButtons from "@/components/offer/OfferActionButtons";
import OfferModals from "@/components/offer/OfferModals";

export default function OfferDetailPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, geo } = useAuth();
  const { getStatus } = useUnifiedStatus();
  const { mode } = useTheme();
  const { getText } = useCopyBank();
  const { requireAuth, gateProps } = useAuthGate();
  const dealStatus = getStatus(businessId);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifications, setVerifications] = useState([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyResult, setVerifyResult] = useState("");
  const [verifyNotes, setVerifyNotes] = useState("");
  const [denialReason, setDenialReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tracker, setTracker] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [userHasVerified, setUserHasVerified] = useState(false);
  const [priceReportStep, setPriceReportStep] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showSubmitDeal, setShowSubmitDeal] = useState(false);
  const [birthdayOffers, setBirthdayOffers] = useState([]);
  const [expandedOfferId, setExpandedOfferId] = useState(location.state?.expandedOfferId || null);
  const [prActualValue, setPrActualValue] = useState("");
  const [prWhatReceived, setPrWhatReceived] = useState("");
  const [prLocationCity, setPrLocationCity] = useState("");
  const [prNotes, setPrNotes] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [showWaitSheet, setShowWaitSheet] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [checklistSteps, setChecklistSteps] = useState({});
  const [recentChange, setRecentChange] = useState(null);
  const timerRefs = useRef([]);

  useEffect(() => {
    return () => { timerRefs.current.forEach(clearTimeout); timerRefs.current = []; };
  }, []);

  // Referral: fire genuine action after 10 seconds on page
  useEffect(() => {
    const t = setTimeout(() => {
      axios.post(`${API}/friends/referral/record-action`, { action_type: "offer_detail_view_10s" }).catch(() => {});
    }, 10000);
    timerRefs.current.push(t);
    return () => clearTimeout(t);
  }, []);

  const fetchOffer = useCallback(async () => {
    try {
      const [offerRes, verifRes, trackerRes, favsRes, funnelRes] = await Promise.all([
        axios.get(`${API}/offers/${businessId}`),
        axios.get(`${API}/offers/${businessId}/verifications`),
        axios.get(`${API}/tracker`),
        axios.get(`${API}/favorites`),
        axios.get(`${API}/offers/${businessId}/funnel`).catch(() => ({ data: null })),
      ]);
      setOffer(offerRes.data);
      trackDealViewed(offerRes.data.business_id, offerRes.data.name);
      // v1.0.0 GATE 3 — PostHog offer_viewed
      trackOfferViewed(
        offerRes.data.business_id,
        offerRes.data.offer_type || null,
        offerRes.data.listing_type || null,
        null,
        location.state?.source || "direct"
      );
      setVerifications(verifRes.data);
      const trackerItem = trackerRes.data.find(t => t.business_id === businessId);
      setTracker(trackerItem);
      setIsFavorited(favsRes.data.map(f => f.business_id).includes(businessId));
      setFunnel(funnelRes.data);
      setUserHasVerified(verifRes.data.some(v => v.user_id === user?.id));
      axios.get(`${API}/business/${businessId}/changes?days=30`)
        .then(r => { const c = r.data.changes || []; if (c.length > 0) setRecentChange(c[0]); })
        .catch(() => {});
    } catch (e) { console.error("Fetch error:", e); trackError("offer_detail_fetch", e, { business_id: businessId }); }
    finally { setLoading(false); }
  }, [businessId, user?.id]);

  useEffect(() => { fetchOffer(); }, [fetchOffer]);
  useEffect(() => { if (businessId) trackPageView("offer_detail", { business_id: businessId }); }, [businessId]);

  // v5.7.0: Fetch birthday_offers for this business (multi-offer support)
  useEffect(() => {
    if (!businessId) return;
    axios.get(`${API}/birthday-offers/business/${businessId}`)
      .then(r => setBirthdayOffers(r.data.offers || []))
      .catch(() => {});
  }, [businessId]);

  const handleVerify = async (resultOverride) => {
    const result = resultOverride || verifyResult;
    if (!result) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/verify`, {
        business_id: businessId, result, notes: verifyNotes, denial_reason: denialReason,
        checklist_steps: Object.keys(checklistSteps).length > 0 ? checklistSteps : undefined,
      });
      setVerifyResult(""); setVerifyNotes(""); setDenialReason(""); setChecklistSteps({});
      if (result === "confirmed") {
        setShowCelebration(true);
        const helpedCount = offer?.favorites_count || 0;
        toast.success(helpedCount > 0 ? `You just helped ${helpedCount} ${helpedCount === 1 ? "person" : "people"} confirm this deal!` : "Verified! Others will thank you for confirming this deal.", { duration: 4000 });
        timerRefs.current.push(setTimeout(() => setShowCelebration(false), 3000));
      } else { toast.success("Thank you for your report! You earned points.", { duration: 3000 }); }
      fetchOffer();
      return result;
    } catch (e) { console.error(e); trackError("verification_submit", e, { business_id: businessId }); }
    finally { setSubmitting(false); }
  };

  const handleTracker = async (action) => {
    try {
      const payload = { business_id: businessId };
      if (action === "signup") payload.signed_up = true;
      else if (action === "claimed") {
        payload.claimed = true;
        if (geo?.coords) { payload.latitude = geo.coords.lat; payload.longitude = geo.coords.lng; }
      }
      if (action === "remove") { await axios.delete(`${API}/tracker/${businessId}`); }
      else {
        await axios.post(`${API}/tracker`, payload);
        trackOfferSaved(businessId, offer?.offer_type || null, null);
        if (action === "claimed") trackEvent("offer_claimed", { business_id: businessId, business_name: offer?.name });
      }
      fetchOffer();
    } catch (e) { console.error(e); trackError("tracker_action", e, { business_id: businessId }); }
  };

  const handleClaimAndVerify = async () => {
    setSubmitting(true);
    try {
      const claimPayload = { business_id: businessId, claimed: true };
      if (geo?.coords) { claimPayload.latitude = geo.coords.lat; claimPayload.longitude = geo.coords.lng; }
      if (!tracker) claimPayload.signed_up = true;
      await Promise.all([
        axios.post(`${API}/tracker`, claimPayload),
        axios.post(`${API}/verify`, { business_id: businessId, result: "confirmed", notes: "Verified via app claim", verification_method: "app-check" }),
      ]);
      setShowCelebration(true);
      trackEvent("offer_claimed", { business_id: businessId, business_name: offer?.name, method: "claim_and_verify" });
      const helpedCount = offer?.favorites_count || 0;
      toast.success(helpedCount > 0 ? `You just helped ${helpedCount} ${helpedCount === 1 ? "person" : "people"} confirm this deal!` : "You're the first to verify — others will thank you!", { duration: 4000 });
      timerRefs.current.push(setTimeout(() => { setShowCelebration(false); setShowFeedback(true); }, 3000));
      fetchOffer();
    } catch (e) { console.error(e); trackError("claim_and_verify", e, { business_id: businessId }); }
    finally { setSubmitting(false); }
  };

  const toggleFavorite = async () => {
    requireAuth("follow", async () => {
      try {
        const res = await axios.post(`${API}/favorites/${businessId}`);
        setIsFavorited(res.data.favorited);
        // v1.0.0 GATE 3 — PostHog offer_saved / offer_unsaved
        if (res.data.favorited) {
          trackOfferSaved(businessId, offer?.offer_type || null, null);
        } else {
          trackOfferUnsaved(businessId);
        }
      } catch (e) { console.error(e); trackError("favorite_toggle", e, { business_id: businessId }); }
    });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--theme-primary)" }} /></div>;
  if (!offer) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Offer not found</p></div>;

  const confidenceColor = offer.confidence_label === "High" ? "text-emerald-600 bg-emerald-50" : offer.confidence_label === "Medium" ? "text-amber-600 bg-amber-50" : "text-slate-600 bg-slate-50";
  const cc = getCategoryColor(offer.category);
  const vd = getValueDisplay(offer);
  const closesIn = offer.hours_status?.minutes_until_close;
  const closesLabel = closesIn != null ? closesIn < 180 ? `${Math.round(closesIn / 60)}h left` : `${Math.round(closesIn / 60)}h` : offer.hours_status?.status_label || "—";
  const closesUrgent = closesIn != null && closesIn < 180;
  const confidence = offer.confidence_score || offer.verification_count || 0;

  const ctx = {
    offer, businessId, user, geo, cc, vd, mode, navigate, dealStatus,
    closesLabel, closesUrgent, confidence, confidenceColor, funnel,
    tracker, isFavorited, userHasVerified, recentChange,
    showCelebration, submitting, setSubmitting, checklistSteps, setChecklistSteps,
    showVerifyModal, setShowVerifyModal, showShare, setShowShare,
    showBugReport, setShowBugReport, showFeedback, setShowFeedback,
    showWaitSheet, setShowWaitSheet,
    priceReportStep, setPriceReportStep,
    prActualValue, setPrActualValue, prWhatReceived, setPrWhatReceived,
    prLocationCity, setPrLocationCity, prNotes, setPrNotes,
    verifyResult, setVerifyResult,
    handleVerify, handleTracker, handleClaimAndVerify, toggleFavorite,
    requireAuth, fetchOffer, getText,
  };

  return (
    <OfferDetailContext.Provider value={ctx}>
      <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="offer-detail-page">
        <OfferHero />
        <div style={{ paddingLeft: 'var(--page-pad)', paddingRight: 'var(--page-pad)', paddingTop: 'var(--card-gap)', paddingBottom: '6rem' }}>
          <OfferSummaryCard />
          <OfferDetailsCard />
          <OfferConfidenceCard />
          <OfferVerificationSection />
          <OfferActionButtons />
        </div>
        {/* v5.7.0: Multi-offer accordion */}
        {birthdayOffers.length > 1 && (
          <div style={{ paddingLeft: 'var(--page-pad)', paddingRight: 'var(--page-pad)' }} data-testid="multi-offer-accordion">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 mb-3">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">{birthdayOffers.length} Offers Available</p>
              {birthdayOffers.map((bo) => {
                const isExpanded = expandedOfferId === bo.offer_id;
                const staleDot = { fresh: "bg-emerald-500", recent: "bg-yellow-400", stale: "bg-amber-500", unknown: "bg-slate-400" };
                return (
                  <div key={bo.offer_id} className="border-b border-slate-100 last:border-0" data-testid={`offer-row-${bo.offer_id}`}>
                    <button onClick={() => setExpandedOfferId(isExpanded ? null : bo.offer_id)} className="w-full text-left py-2.5 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${staleDot[bo.staleness_level] || staleDot.unknown}`} />
                      <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{bo.offer_title}</span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700">{bo.offer_type?.replace(/_/g, " ")}</span>
                      {bo.is_true_freebie && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">Free</span>}
                      {bo.loyalty_program_required && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-100 text-violet-700">Loyalty</span>}
                      <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="pl-4 pb-3 space-y-2" data-testid={`offer-expanded-${bo.offer_id}`}>
                        {bo.offer_plain_language_summary && <p className="text-sm text-slate-600">{bo.offer_plain_language_summary}</p>}
                        {bo.redemption_steps?.length > 0 && (
                          <ol className="space-y-1">{bo.redemption_steps.map((s, i) => (
                            <li key={i} className="flex gap-1.5 text-xs text-slate-500"><span className="font-bold text-amber-600">{i + 1}.</span>{s}</li>
                          ))}</ol>
                        )}
                        <button onClick={() => navigate(`/birthday-offer/${bo.offer_id}`, { state: { from: `/offer/${businessId}`, parentState: { expandedOfferId: bo.offer_id } } })} className="text-xs font-semibold text-amber-600 hover:text-amber-700" data-testid={`learn-more-${bo.offer_id}`}>
                          Learn More →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* v5.7.0: Submit a Deal button */}
        <div style={{ paddingLeft: 'var(--page-pad)', paddingRight: 'var(--page-pad)', paddingBottom: '1rem' }}>
          <button onClick={() => requireAuth("submit_deal", () => setShowSubmitDeal(true))} className="w-full py-2.5 rounded-xl border border-slate-300 text-slate-500 font-semibold text-xs hover:bg-slate-50 transition-colors" data-testid="submit-deal-open-btn">
            Know a deal here? Submit it
          </button>
        </div>
        <OfferModals />
        <div className="px-5 py-4 text-center" data-testid="liability-disclaimer">
          <p className="text-[10px] text-slate-400 leading-relaxed">
            Information is crowd-sourced and may not be current. Always call ahead or check the official website to confirm availability.
            BirthdayDeaLight is not affiliated with any listed business. Offers subject to change without notice.
          </p>
        </div>
        <BottomNav />
        <TooltipOverlay screen="offer_detail" targetSelector='[data-testid="tracker-signup-btn"]' message="Tap here to track this deal and get reminders before your birthday!" />
        <AuthGateSheet {...gateProps} />
        {showSubmitDeal && <SubmitDealSheet businessId={businessId} businessName={offer?.name || ""} onClose={() => setShowSubmitDeal(false)} />}
      </div>
    </OfferDetailContext.Provider>
  );
}
